import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/standards", label: "National Standards", short: "Standards" },
  { to: "/certification", label: "Certification" },
  { to: "/registry", label: "Find a Practitioner" },
  { to: "/education", label: "Education" },
  { to: "/healthcare", label: "Healthcare" },
  { to: "/gallery", label: "Our Work" },
  { to: "/resources", label: "Resources" },
  { to: "/news", label: "News & Advocacy", short: "News" },
  { to: "/contact", label: "Contact" },
];

export const SERVICES = [
  "3D Areola Restoration",
  "Scar Camouflage",
  "Vitiligo Camouflage",
  "Burn Scar Restoration",
  "Other",
];

export const PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
  "Northwest Territories", "Nunavut", "Yukon",
];

export function Button({ children, to, onClick, variant = "primary", className = "", type, "data-testid": testId, disabled }) {
  const base = "inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300 disabled:opacity-50";
  const variants = {
    primary: "bg-ccpm-olive text-ccpm-ivory hover:bg-ccpm-olive-deep",
    outline: "border border-ccpm-olive text-ccpm-olive hover:bg-ccpm-olive hover:text-ccpm-ivory",
    ghost: "text-ccpm-charcoal hover:text-ccpm-olive border-b border-transparent hover:border-ccpm-olive",
    light: "bg-ccpm-ivory text-ccpm-charcoal hover:bg-ccpm-stone",
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  return <button type={type || "button"} onClick={onClick} className={cls} data-testid={testId} disabled={disabled}>{children}</button>;
}

export function PageHero({ overline, title, subtitle, testId }) {
  return (
    <header className="relative border-b border-ccpm-border bg-ccpm-stone/50" data-testid={testId}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-20">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="overline text-ccpm-olive mb-6">{overline}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.08 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ccpm-charcoal max-w-4xl">{title}</motion.h1>
        {subtitle && (
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-ccpm-taupe font-light">{subtitle}</motion.p>
        )}
      </div>
    </header>
  );
}

export function Section({ children, className = "", id }) {
  return <section id={id} className={`mx-auto max-w-7xl px-6 lg:px-10 ${className}`}>{children}</section>;
}

export function Divider() {
  return <div className="mx-auto max-w-7xl px-6 lg:px-10"><div className="h-px bg-ccpm-border" /></div>;
}

export function PullQuote({ children, sub = false, dark = false, className = "" }) {
  return (
    <div className={`mx-auto max-w-7xl px-6 lg:px-10 ${className}`}>
      <figure className={`py-16 lg:py-24 text-center border-t border-b ${dark ? "border-white/15" : "border-ccpm-border"}`}>
        <span className={`font-serif text-6xl leading-none ${dark ? "text-ccpm-olive-soft/50" : "text-ccpm-olive/30"}`}>&ldquo;</span>
        <blockquote className={`-mt-6 font-serif italic ${sub ? "text-2xl sm:text-3xl lg:text-4xl" : "text-3xl sm:text-4xl lg:text-5xl"} leading-[1.18] max-w-4xl mx-auto ${dark ? "text-ccpm-ivory" : "text-ccpm-charcoal"}`}>
          {children}
        </blockquote>
      </figure>
    </div>
  );
}
